import React, { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { Copy, Loader2, Pencil, Save, Trash2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { formatDate } from "../../utils/date/common"

const fieldClass =
  "h-10 w-full rounded-input border border-app-content-faint/30 bg-surface-main px-3 text-app-content " +
  "outline-none transition-colors focus:border-brand-main focus:ring-2 focus:ring-brand-main/20 " +
  "disabled:opacity-disabled disabled:cursor-not-allowed"

const onlyNumber = (v) =>
  String(v)
    .replace(",", ".")
    .replace(/[^0-9.]/g, "")

const str = (v) => (v === null || v === undefined ? "" : String(v))

// Значения из grid-JSON -> черновик формы (ключи совпадают с атрибутами Price)
const toDraft = (p = {}, fallback = {}) => ({
  transfer_type: p.transfer_type ?? fallback.transfer_type ?? "group",
  valute_id: str(p.valute_id ?? fallback.valute_id),
  adult_sum: str(p.adult),
  adult_sum_netto: str(p.adult_netto),
  passenger_from: str(p.passenger_from ?? 1),
  passenger_to: str(p.passenger_to ?? 100),
  child_sum: str(p.child),
  child_sum_netto: str(p.child_netto),
  infant_sum: str(p.infant),
  infant_sum_netto: str(p.infant_netto),
  child_age_max: str(p.child_age_max ?? fallback.child_age_max ?? 11.99),
  infant_age_max: str(p.infant_age_max ?? fallback.infant_age_max ?? 1.99),
})

const Caption = ({ children }) => (
  <span className="mb-1 block text-[11px] text-text-muted">{children}</span>
)

const Labeled = ({ label, className = "", children }) => (
  <label className={`block ${className}`}>
    <Caption>{label}</Caption>
    {children}
  </label>
)

// Форма блока Cost + тип трансфера. Используется и для правки, и для добавления цены.
const PriceForm = ({
  initial,
  transferTypeOptions,
  valuteOptions,
  currencyName,
  onSave,
  saveLabel,
  alwaysDirty = false,
  info,
  actions,
}) => {
  const { t } = useTranslation()
  const signature = JSON.stringify(initial)
  const [draft, setDraft] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  // после сохранения таблица перезагружается — подтягиваем свежие значения
  useEffect(() => {
    setDraft(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  const dirty = alwaysDirty || Object.keys(initial).some((k) => initial[k] !== draft[k])

  const currencies = useMemo(() => {
    if (valuteOptions?.length) return valuteOptions
    return initial.valute_id
      ? [{ id: initial.valute_id, name: currencyName || initial.valute_id }]
      : []
  }, [valuteOptions, initial.valute_id, currencyName])

  const set = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setError("")
  }

  const num = (key) => (
    <input
      type="text"
      inputMode="decimal"
      value={draft[key]}
      onChange={(e) => set(key, onlyNumber(e.target.value))}
      className={`${fieldClass} text-right`}
    />
  )

  const save = async () => {
    if (!draft.valute_id) {
      return setError(t("admin.currency_required", { defaultValue: "Выберите валюту" }))
    }
    if (draft.adult_sum === "" || Number.isNaN(Number(draft.adult_sum))) {
      return setError(t("admin.price_required", { defaultValue: "Укажите цену" }))
    }
    if (Number(draft.passenger_from) > Number(draft.passenger_to)) {
      return setError(
        t("admin.pax_range_error", { defaultValue: "Пассажиров «от» больше, чем «до»" })
      )
    }

    setSaving(true)
    try {
      await onSave({ ...draft })
    } catch (e) {
      setError(
        (e?.data?.errors ?? [t("admin.save_error", { defaultValue: "Ошибка сохранения" })]).join(
          ", "
        )
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="px-4 py-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
        <Labeled
          label={t("admin.transfer_type", { defaultValue: "Тип трансфера" })}
          className="col-span-2"
        >
          <select
            value={draft.transfer_type}
            onChange={(e) => set("transfer_type", e.target.value)}
            className={fieldClass}
          >
            {transferTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Labeled>

        <Labeled label={t("admin.currency", { defaultValue: "Валюта" })} className="col-span-2">
          <select
            value={draft.valute_id}
            onChange={(e) => set("valute_id", e.target.value)}
            className={fieldClass}
          >
            <option value="" />
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Labeled>

        <Labeled label={t("admin.price_adult", { defaultValue: "Цена за взрослого" })}>
          {num("adult_sum")}
        </Labeled>
        <Labeled label={t("admin.price_adult_netto", { defaultValue: "Нетто за взрослого" })}>
          {num("adult_sum_netto")}
        </Labeled>
        <Labeled label={t("admin.pax_from", { defaultValue: "Пассажиров от" })}>
          {num("passenger_from")}
        </Labeled>
        <Labeled label={t("admin.pax_to", { defaultValue: "Пассажиров до" })}>
          {num("passenger_to")}
        </Labeled>

        <Labeled label={t("admin.price_child", { defaultValue: "Цена за ребёнка" })}>
          {num("child_sum")}
        </Labeled>
        <Labeled label={t("admin.price_child_netto", { defaultValue: "Нетто за ребёнка" })}>
          {num("child_sum_netto")}
        </Labeled>
        <Labeled label={t("admin.price_infant", { defaultValue: "Цена за младенца" })}>
          {num("infant_sum")}
        </Labeled>
        <Labeled label={t("admin.price_infant_netto", { defaultValue: "Нетто за младенца" })}>
          {num("infant_sum_netto")}
        </Labeled>

        <Labeled label={t("admin.child_age_max", { defaultValue: "Макс. возраст ребёнка" })}>
          {num("child_age_max")}
        </Labeled>
        <Labeled label={t("admin.infant_age_max", { defaultValue: "Макс. возраст младенца" })}>
          {num("infant_age_max")}
        </Labeled>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-app-content-faint">{info}</p>

        <div className="flex items-center gap-1">
          {error && (
            <motion.span
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              className="mr-2 text-xs text-red-500"
            >
              {error}
            </motion.span>
          )}

          {actions}

          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="ml-1 inline-flex h-9 items-center gap-1.5 rounded-button bg-app-base-strong px-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-app-base disabled:cursor-not-allowed disabled:opacity-disabled"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saveLabel || t("admin.save", { defaultValue: "Сохранить" })}
          </button>
        </div>
      </div>
    </div>
  )
}

// Одна существующая цена
const PriceRow = ({
  price,
  transferTypeOptions,
  valuteOptions,
  onSave,
  onDelete,
  busy,
  editUrl,
  copyUrl,
  showCopy,
}) => {
  const { t } = useTranslation()
  const initial = useMemo(() => toDraft(price), [price])

  return (
    <li>
      <PriceForm
        initial={initial}
        transferTypeOptions={transferTypeOptions}
        valuteOptions={valuteOptions}
        currencyName={price.currency}
        onSave={(attrs) => onSave(price.id, attrs)}
        info={
          (price.from_date || price.to_date) &&
          `${formatDate(price.from_date)} – ${formatDate(price.to_date)}`
        }
        actions={
          <>
            <a
              href={editUrl}
              title={t("admin.open_full_form", { defaultValue: "Открыть полную форму" })}
              className="p-1.5 text-text-secondary hover:text-brand-main"
            >
              <Pencil className="w-4 h-4" />
            </a>

            {showCopy && (
              <a
                href={copyUrl}
                title={t("admin.copy", { defaultValue: "Копировать" })}
                className="p-1.5 text-text-secondary hover:text-brand-main"
              >
                <Copy className="w-4 h-4" />
              </a>
            )}

            <button
              type="button"
              disabled={busy}
              onClick={() => onDelete([price.id])}
              title={t("admin.delete", { defaultValue: "Удалить" })}
              className="p-1.5 text-red-500 hover:text-red-600 disabled:opacity-disabled"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </>
        }
      />
    </li>
  )
}

// Модалка БЕЗ портала: фиксированный оверлей рендерится прямо в дереве ячейки.
// mode="edit" — список цен ячейки; mode="add" — одна форма новой цены для трассы.
const PriceEditModal = ({
  mode = "edit",
  title,
  subtitle,
  prices = [],
  transferTypeOptions = [],
  valuteOptions = [],
  buildEditUrl,
  buildNewUrl,
  baseId,
  typeId,
  typeParam,
  template, // любая существующая цена трассы — источник валюты/типа/возрастов по умолчанию
  onSave,
  onCreate,
  onDelete,
  busyIds = [],
  onClose,
  showCopy = false,
}) => {
  const { t } = useTranslation()
  const isAdd = mode === "add"

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose()
    const body = document.body
    const prevOverflow = body.style.overflow
    const prevPaddingRight = body.style.paddingRight

    // Без скроллбара страница стала бы шире и «прыгнула» — добавляем отступ на его ширину
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    if (scrollbarWidth > 0) {
      const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0
      body.style.paddingRight = `${current + scrollbarWidth}px`
    }
    body.style.overflow = "hidden"
    document.addEventListener("keydown", onKey)

    return () => {
      document.removeEventListener("keydown", onKey)
      body.style.overflow = prevOverflow
      body.style.paddingRight = prevPaddingRight
    }
  }, [onClose])

  const addInitial = useMemo(
    () =>
      toDraft(
        {},
        {
          transfer_type: template?.transfer_type,
          valute_id: template?.valute_id,
          child_age_max: template?.child_age_max,
          infant_age_max: template?.infant_age_max,
        }
      ),
    [template]
  )

  const handleCreate = async (attrs) => {
    // typeId 0 = «без типа»
    await onCreate(baseId, { ...attrs, transport_type_id: typeId ? typeId : "" })
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 text-left"
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        className="absolute inset-0 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-card bg-white shadow-lg"
      >
        <div className="flex items-start gap-3 border-b border-app-content-faint/10 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-app-base-strong leading-snug break-words">{title}</p>
            {subtitle && <p className="mt-0.5 text-xs text-text-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            title={t("admin.close", { defaultValue: "Закрыть" })}
            className="p-1.5 text-text-secondary hover:text-app-content"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isAdd ? (
          <div className="overflow-y-auto">
            <PriceForm
              initial={addInitial}
              transferTypeOptions={transferTypeOptions}
              valuteOptions={valuteOptions}
              alwaysDirty
              saveLabel={t("admin.add_price", { defaultValue: "Добавить цену" })}
              onSave={handleCreate}
            />
          </div>
        ) : (
          <ul className="divide-y divide-app-content-faint/10 overflow-y-auto">
            {prices.map((p) => (
              <PriceRow
                key={p.id}
                price={p}
                transferTypeOptions={transferTypeOptions}
                valuteOptions={valuteOptions}
                onSave={onSave}
                onDelete={onDelete}
                busy={busyIds.includes(p.id)}
                editUrl={buildEditUrl(p.id)}
                showCopy={showCopy}
                copyUrl={buildNewUrl?.({ from_id: p.id, copy: 1, transport_type_id: typeParam })}
              />
            ))}
          </ul>
        )}
      </motion.div>
    </div>
  )
}

export default PriceEditModal
